import { Box, Stack, Switch, Typography } from "@mui/material";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import {
  pushToHistoryAction,
  setIsClonable,
  setIsLive,
  setIsLocked,
  setIsPrimary,
  setVisibility,
} from "../../../../redux/reducers/segmentBuilder";
import {
  getIsLocked,
  getIsPrimary,
  getVisibility,
  getWheelIsClonableValue,
  getWheelIsLiveValue,
} from "../../../../redux/selectors/segmentBuilderSelectors";
import { colors } from "../../../../utils";
import WarningClonePopup from "../../../BroadcastModal/WarningClonePopup";
import IyugaTooltip from "../../../IyugaTooltip";
import LockSettingsModal from "../../../LockSettingsModal";
import RadioButton from "../../../RadioButton";
import TextView from "../../../TextView";
import BroadCastButton from "../../../LiveCloneComponents/BroadCastButton";
import {
  closeDialogPopup,
  openDialogPopup,
} from "../../../../redux/reducers/dialogPopup";

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${colors.lightBorder};
  margin-top: 10px;
  margin-bottom: 20px;
`;

const PrivacyTab = () => {
  const isLocked = useSelector(getIsLocked);
  const visibility = useSelector(getVisibility);
  const isWheelClonable = useSelector(getWheelIsClonableValue);
  const isLiveClone = useSelector(getWheelIsLiveValue);
  const isPrimaryWheel = useSelector(getIsPrimary);

  const [lockModalOpen, setLockModalOpen] = useState(false);
  const [showDisableLiveCloneModal, setshowDisableLiveCloneModal] =
    useState(false);

  const { id } = useParams();
  const dispatch = useDispatch();

  const handlePublicPrivateHiddenChange = (event) => {
    const newVisibility = event.target.value;

    // Helper function to handle visibility change
    const changeVisibility = () => {
      dispatch(setVisibility(newVisibility));
      dispatch(pushToHistoryAction());
    };

    // Helper function to show confirmation dialog
    const showConfirmationDialog = (title, text) => {
      dispatch(
        openDialogPopup({
          isOpen: true,
          title,
          primaryButtonTitle: "Proceed",
          secondaryButtonTitle: "Cancel",
          text,
          secondaryButtonOnClick: () => dispatch(closeDialogPopup()),
          primaryButtonOnClick: () => {
            dispatch(setIsPrimary(false));
            changeVisibility();
          },
        })
      );
    };

    // Handle different visibility states
    switch (newVisibility) {
      case "2": // Private
        if (isPrimaryWheel) {
          showConfirmationDialog(
            "Make This Wheel Private?",
            "This is a Primary Wheel, making it Private will remove it from being a Primary Wheel."
          );
        } else {
          changeVisibility();
        }
        break;

      case "3": // Hidden
        if (isPrimaryWheel) {
          showConfirmationDialog(
            "Make This Wheel Hidden?",
            "This is a Primary Wheel, making it Hidden will remove it from being a Primary Wheel."
          );
        } else {
          changeVisibility();
        }
        break;

      default: // Public
        changeVisibility();
        break;
    }
  };

  const handleLockedUnlockedChange = (event) => {
    const wantLocked = event.target.value === "true";

    if (wantLocked) {
      // Do NOT set locked yet. Require password first.
      setLockModalOpen(true);
    } else {
      // Unlock immediately
      dispatch(setIsLocked(false));
      dispatch(pushToHistoryAction());
    }
  };

  const handleCanBeClonedChange = (event) => {
    const isChecked = event.target.checked;
    dispatch(setIsClonable(isChecked));

    // If cloneable is turned off, automatically turn off and disable Sync Clones
    if (!isChecked) {
      dispatch(setIsLive(false));
    }
    // dispatch(setCloning(event.target.value === "can be cloned"));
    // dispatch(pushToHistoryAction()); // Push the new state to history
  };

  const handleLiveCloneUpdate = (event) => {
    if (event.target.checked === true) {
      dispatch(setIsLive(event.target.checked));
    } else {
      setshowDisableLiveCloneModal(true);
    }
  };

  const VisibilityTooltip = (
    <div>
      <TextView>
        <b>Public</b> - Wheel is visible to visitors on your gallery
      </TextView>
      <TextView>
        <b>Private</b> - Wheel is NOT visible to visitors, but can be accessed
        with its URL
      </TextView>
      <TextView>
        <b>Hidden</b> - Wheel is NOT visible to others and CANNOT be accessed
        with its URL
      </TextView>
    </div>
  );

  const LockTooltip = (
    <div>
      <TextView>
        <b>Locked</b> - Wheel is locked and can only be accessed with the access
        key
      </TextView>
      <TextView>
        <b>Unlocked</b> - Wheel is unlocked and can be accessed without the
        password
      </TextView>
    </div>
  );

  return (
    <>
      <Box>
        <RadioButton
          label="Wheel Visibility"
          options={[
            { value: 1, label: "Public" },
            { value: 2, label: "Private" },
            { value: 3, label: "Hidden" },
          ]}
          selectedOption={visibility}
          onChange={handlePublicPrivateHiddenChange}
          orientation="vertical"
          tooltip={VisibilityTooltip}
        />

        <Divider />

        <RadioButton
          label="Lock Status"
          options={[
            { value: true, label: "Locked" },
            { value: false, label: "Unlocked" },
          ]}
          selectedOption={isLocked}
          onChange={handleLockedUnlockedChange}
          orientation="vertical"
          tooltip={LockTooltip}
        />

        <Box>
          <span
            onClick={() => isLocked && setLockModalOpen(true)}
            style={{
              fontSize: 14,
              color: isLocked ? "rgba(15, 82, 186, 1)" : colors.lightText,
              cursor: isLocked ? "pointer" : "not-allowed",
              textDecoration: isLocked ? "underline" : "none",
              pointerEvents: isLocked ? "auto" : "none",
              opacity: isLocked ? 1 : 0.5,
            }}
          >
            Advanced Settings
          </span>
        </Box>

        <Divider />
        <Stack alignItems="center" direction={"row"}>
          <TextView variant="subtitle">{"Cloning"}</TextView>
        </Stack>

        <Stack mt={2} direction="column">
          <Box
            display="flex"
            alignItems="center"
            // justifyContent="space-between"
          >
            <Typography
              sx={{
                color: "#565556",
                fontSize: "14px",
                fontFamily: "DM Sans",
              }}
            >
              Can be cloned
            </Typography>
            <Switch
              checked={isWheelClonable}
              onChange={handleCanBeClonedChange}
            />
            <IyugaTooltip
              placement={"right"}
              tooltip={
                <TextView>
                  Users can Clone this Wheel into their Gallery. You will be
                  given Original Author credit for this and any subsequent
                  Clones.
                </TextView>
              }
            ></IyugaTooltip>
          </Box>
          <Box display="flex" alignItems="center">
            <Typography
              sx={{
                color: "#565556",
                fontSize: "14px",
                fontFamily: "DM Sans",
              }}
            >
              Sync Clones
            </Typography>
            <Switch
              checked={isLiveClone}
              onChange={handleLiveCloneUpdate}
              disabled={!isWheelClonable}
            />
            <IyugaTooltip
              placement={"top"}
              tooltip={
                <div>
                  <TextView>
                    If enabled, Clones of this Wheel will be ‘Sync Clones’. Sync
                    Clones are connected to the Original and will be
                    automatically updated after pressing{" "}
                    <BroadCastButton size="small" /> in the header. <br />
                    Sync Clones cannot be edited by anyone other than the
                    original creator.
                  </TextView>
                </div>
              }
            ></IyugaTooltip>
          </Box>

          {/* Disclaimer - only show if both Cloneable and Sync Clones are enabled */}
          {isWheelClonable && isLiveClone && (
            <Box
              sx={{
                width: "100%",
                minHeight: "72px",
                gap: "8px",
                borderRadius: "12px",
                border: "1px solid #F79A00",
                padding: "16px",
                backgroundColor: "#F79A001A",
                marginTop: "16px",
                marginBottom: "30px",
                display: "flex",
                alignItems: "center",
                justifyContent: "start",
              }}
            >
              <TextView
                style={{
                  fontFamily: "DM Sans",
                  fontWeight: 400,
                  fontSize: "14px",
                  lineHeight: "20px",
                  letterSpacing: "0%",
                  color: "#565556",
                }}
              >
                Other Users can only edit clones if "Sync Clones" is turned off.
              </TextView>
            </Box>
          )}
        </Stack>
      </Box>
      <LockSettingsModal
        isOpen={lockModalOpen}
        handleClose={() => setLockModalOpen(false)}
        wheelId={id}
      />

      <WarningClonePopup
        handleClose={() => setshowDisableLiveCloneModal(false)}
        isOpen={showDisableLiveCloneModal}
        onAccept={() => {
          setshowDisableLiveCloneModal(false);
          dispatch(setIsLive(false));
        }}
      >
        <Stack gap={2} mt={2}>
          <TextView color={"#565556"} variant="subtitle">
            Are you sure?
          </TextView>
          <TextView color={"#565556"} variant="subtitle">
            Are you sure? Disabling Sync Clones will permanently disconnect this
            Wheel from all previously created Clones.
          </TextView>
          <TextView color={"#565556"} variant="subtitle">
            Re-enabling this Feature will only sync future Clones.
          </TextView>
        </Stack>
      </WarningClonePopup>
    </>
  );
};

export default PrivacyTab;
